import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, InputAdornment, Menu, MenuItem, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface ColumnFilterMenuProps {
  anchorEl: HTMLElement | null;
  /** Valores únicos de la columna, ya ordenados. */
  values: string[];
  /** Valores actualmente seleccionados. Vacío = columna sin filtrar. */
  selected: string[];
  onClose: () => void;
  /** Recibe la selección completa ya calculada. */
  onChange: (selected: string[]) => void;
}

/**
 * Menú de filtro por columna: buscador de texto + "Seleccionar todo" + checkboxes.
 *
 * El buscador es imprescindible en columnas con muchos valores (cargos, actividades):
 * sin él hay que recorrer la lista completa a mano.
 */
const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = ({
  anchorEl,
  values,
  selected,
  onClose,
  onChange,
}) => {
  const [search, setSearch] = useState('');

  // Al cerrar se limpia la búsqueda para que el próximo filtro parta en blanco.
  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const visibleValues = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return values;
    return values.filter(value => value.toLowerCase().includes(term));
  }, [values, search]);

  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(value => selected.includes(value));
  const someVisibleSelected = visibleValues.some(value => selected.includes(value)) && !allVisibleSelected;

  const toggleValue = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter(item => item !== value)
        : [...selected, value]
    );
  };

  // "Seleccionar todo" actúa solo sobre lo que se está viendo, de modo que buscar
  // "operador" y marcar todo no descarte los filtros ya puestos en otros valores.
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      onChange(selected.filter(value => !visibleValues.includes(value)));
    } else {
      onChange(Array.from(new Set([...selected, ...visibleValues])));
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      MenuListProps={{ 'aria-labelledby': 'filter-menu-button', autoFocusItem: false }}
      PaperProps={{ style: { maxHeight: 400, minWidth: 260 } }}
    >
      <Box sx={{ p: 1, pt: 0.5, backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
        <TextField
          fullWidth
          autoFocus
          variant="standard"
          placeholder="Buscar valor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          // MUI MenuList usa las teclas para saltar al ítem que empieza con esa letra y
          // se lleva el foco del input: sin este stopPropagation solo se pueden escribir
          // números (ningún ítem empieza con dígito, así que esos no disparaban el salto).
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <MenuItem onClick={toggleAllVisible} disabled={visibleValues.length === 0}>
        <FormControlLabel
          control={<Checkbox checked={allVisibleSelected} indeterminate={someVisibleSelected} />}
          label={search.trim() ? 'Seleccionar lo encontrado' : 'Seleccionar todo'}
          sx={{ pointerEvents: 'none' }}
        />
      </MenuItem>

      {visibleValues.length === 0 && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Sin resultados</Typography>
        </Box>
      )}

      {visibleValues.map((value) => (
        <MenuItem key={value} onClick={() => toggleValue(value)}>
          <FormControlLabel
            control={<Checkbox checked={selected.includes(value)} />}
            label={value}
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ColumnFilterMenu;
